/**
 * SAB-582: processor fee arithmetic.
 *
 * Everything here works in INTEGER CENTS. Do not reintroduce floats.
 * 10.87 * 0.0349 in IEEE 754 is not what you think it is, and this module
 * decides how much real money leaves a player's account.
 *
 * Vocabulary, matching the wallet_transactions columns:
 *   gross  = what the processor charges the payer
 *   fee    = what the processor keeps
 *   net    = what lands in the business account (gross - fee)
 *   amount = what we credit to the wallet (the player liability)
 *
 * Under the gross-up model (option A), net === amount by construction.
 */

export type ProcessorId = "paypal" | "aeropay" | "soap";

export interface FeeSchedule {
  /** Percentage component in basis points. 349 = 3.49%. */
  percentBps: number;
  /** Fixed component in cents. 49 = $0.49. */
  fixedCents: number;
  /** False until the real numbers are confirmed with the vendor. */
  confirmed: boolean;
}

export const DEPOSIT_FEE_SCHEDULES: Record<ProcessorId, FeeSchedule> = {
  // PayPal standard commercial rate, verified against live capture responses
  // on 2026-07-29 and 07-30: $10.00 gross -> $0.84 fee -> $9.16 net.
  paypal: { percentBps: 349, fixedCents: 49, confirmed: true },

  // TODO(SAB-582): confirm with Reid or Ayan. Placeholder only.
  // Do NOT ship a gross-up against an unconfirmed schedule; see assertConfirmed.
  aeropay: { percentBps: 0, fixedCents: 0, confirmed: false },

  // TODO(SAB-582): capture from Soap's fee structure during sandbox work.
  soap: { percentBps: 0, fixedCents: 0, confirmed: false },
};

/** Half-up rounding on a non-negative rational, in integer arithmetic. */
function roundHalfUp(numerator: number, denominator: number): number {
  return Math.floor((numerator * 2 + denominator) / (denominator * 2));
}

/**
 * The fee a processor takes on a given gross charge.
 * Modelled as half-up to the cent, which matches every observed PayPal capture.
 */
export function processorFeeCents(grossCents: number, s: FeeSchedule): number {
  if (!Number.isInteger(grossCents) || grossCents <= 0) {
    throw new Error(`grossCents must be a positive integer, got ${grossCents}`);
  }
  return roundHalfUp(grossCents * s.percentBps, 10_000) + s.fixedCents;
}

/** What actually lands in the business account for a given gross charge. */
export function netFromGrossCents(grossCents: number, s: FeeSchedule): number {
  return grossCents - processorFeeCents(grossCents, s);
}

/**
 * The charge required for `desiredCreditCents` to land net.
 *
 * charge = (desired + fixed) / (1 - rate), rounded UP to the cent.
 *
 * Adding the fee to the base amount instead is WRONG: it reproduces the original
 * shortfall in miniature ($10 + 8.4% = $10.84 nets $9.97, still three cents short).
 *
 * The verification loop at the end is deliberate. It makes the result correct
 * regardless of how the processor rounds its own fee, rather than trusting our
 * model of their rounding. It runs at most a couple of iterations.
 */
export function grossUpCents(desiredCreditCents: number, s: FeeSchedule): number {
  if (!Number.isInteger(desiredCreditCents) || desiredCreditCents <= 0) {
    throw new Error(`desiredCreditCents must be a positive integer, got ${desiredCreditCents}`);
  }
  assertConfirmed(s);

  const numerator = (desiredCreditCents + s.fixedCents) * 10_000;
  const denominator = 10_000 - s.percentBps;
  let gross = Math.ceil(numerator / denominator);

  // Never round down into a shortfall.
  let guard = 0;
  while (netFromGrossCents(gross, s) < desiredCreditCents) {
    gross += 1;
    if (++guard > 10) {
      throw new Error(`grossUpCents failed to converge for ${desiredCreditCents}`);
    }
  }

  // Then take the smallest charge that still clears. Because the processor rounds
  // its own fee to the cent, the ceiling is often one cent more than necessary,
  // which would land net one cent ABOVE the credited amount. That is harmless to
  // the player but it breaks `net_amount = amount` as an exact identity, and that
  // identity is what makes the reconciliation view provable rather than approximate.
  guard = 0;
  while (gross > 1 && netFromGrossCents(gross - 1, s) >= desiredCreditCents) {
    gross -= 1;
    if (++guard > 10) break;
  }
  return gross;
}

/** Full breakdown for a deposit, ready to write to wallet_transactions. */
export interface DepositBreakdown {
  /** wallet_transactions.amount, the credited liability */
  amountCents: number;
  /** wallet_transactions.gross_amount, what we charge */
  grossCents: number;
  /** wallet_transactions.processor_fee_amount */
  feeCents: number;
  /** wallet_transactions.net_amount */
  netCents: number;
  /** wallet_transactions.processor */
  processor: ProcessorId;
}

export function buildDeposit(
  desiredCreditCents: number,
  processor: ProcessorId,
): DepositBreakdown {
  const schedule = DEPOSIT_FEE_SCHEDULES[processor];
  const grossCents = grossUpCents(desiredCreditCents, schedule);
  const feeCents = processorFeeCents(grossCents, schedule);
  const netCents = grossCents - feeCents;

  // The invariant the whole ticket exists to protect.
  if (netCents < desiredCreditCents) {
    throw new Error(
      `gross-up invariant violated: net ${netCents} < credited ${desiredCreditCents}`,
    );
  }
  return { amountCents: desiredCreditCents, grossCents, feeCents, netCents, processor };
}

function assertConfirmed(s: FeeSchedule): void {
  if (!s.confirmed) {
    throw new Error(
      "fee schedule is unconfirmed; confirm the vendor's real rate before charging users",
    );
  }
}

/** Display helper. Cents to a plain dollar string, no locale surprises. */
export function formatCents(cents: number): string {
  const sign = cents < 0 ? "-" : "";
  const abs = Math.abs(cents);
  return `${sign}$${Math.floor(abs / 100)}.${String(abs % 100).padStart(2, "0")}`;
}
